import React, { CSSProperties } from 'react';
import { clsx, styx } from 'modules/Utils';
import Colors from 'modules/Colors';
import Icon from 'elements/Icon';
import ButtonSymphony from 'elements/ButtonSymphony';
import useScreen from 'Hooks/useScreen';

export interface ConnectionItem {
    id: string;
    name: string;
    icon: string;
    isConnected: boolean;
    connectedText?: string;
    subtitle?: string;
}

interface ConnectionsListProps {
    /** Optional class name for styling */
    className?: string;
    /** Array of connection items to display */
    connections: ConnectionItem[];
    /** Handler for when a connection is clicked */
    onConnectionClick: (connection: ConnectionItem) => void;
    /** Handler for when remove connection is clicked */
    onRemoveConnection?: (connection: ConnectionItem) => void;
    /** Whether to show remove buttons for connected items */
    showRemoveButton?: boolean;
}

const ConnectionsList = ({
    className,
    connections,
    onConnectionClick,
    onRemoveConnection,
    showRemoveButton = false,
}: ConnectionsListProps) => {
    const { mobileView } = useScreen();

    const renderCheckmark = () => (
        <div className="checkmarkContainer" style={sty.checkmarkContainer}>
            <Icon
                className="checkmarkIcon"
                icon="check"
                size={16}
                color={Colors.white}
            />
        </div>
    );

    return (
        <div className={clsx('connectionsList', className)} style={sty.connectionsList}>
            {connections.map((connection) => (
                <div
                    key={connection.id}
                    className={clsx('connectionItem', connection.isConnected && 'connected')}
                    style={styx(
                        sty.connectionItem,
                        connection.isConnected ? sty.connectionItemConnected : {}
                    )}
                >
                    <div className="connectionIcon" style={sty.connectionIcon}>
                        <Icon
                            className="platformIcon"
                            icon={connection.icon}
                            size={24}
                        />
                        {connection.isConnected && renderCheckmark()}
                    </div>
                    
                    <div className="connectionContent" style={sty.connectionContent}>
                        <div className="connectionName" style={sty.connectionName}>
                            {connection.name}
                        </div>
                        {connection.isConnected && connection.connectedText ? (
                            <div className="connectionConnectedText" style={sty.connectionConnectedText}>
                                {connection.connectedText}
                            </div>
                        ) : connection.subtitle ? (
                            <div className="connectionSubtitle" style={sty.connectionSubtitle}>
                                {connection.subtitle}
                            </div>
                        ) : null}
                    </div>

                    <div className="connectionActions" style={sty.connectionActions}>
                        {showRemoveButton && connection.isConnected && onRemoveConnection && (
                            <div
                                className="removeConnectionButton"
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    onRemoveConnection(connection);
                                }}
                                style={sty.removeConnectionButton}
                            >
                                <Icon
                                    className="removeIcon"
                                    icon="close"
                                    size={16}
                                    color={Colors.grey}
                                />
                            </div>
                        )}
                        
                        <ButtonSymphony
                            className="connectionActionButton"
                            onClick={() => onConnectionClick(connection)}
                            variant="text"
                        >
                            {connection.isConnected ? 'Edit' : 'Connect'}
                        </ButtonSymphony>
                    </div>
                </div>
            ))}
        </div>
    );
};

const sty: Record<string, CSSProperties> = {
    connectionsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        width: '100%',
    },
    connectionItem: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
        padding: 16,
        border: `2px solid ${Colors.greyLight}`,
        borderRadius: 12,
        backgroundColor: Colors.white,
        transition: 'all 0.2s ease',
        cursor: 'pointer',
    },
    connectionItemConnected: {
        borderColor: Colors.purple,
        backgroundColor: `${Colors.purple}05`,
    },
    connectionIcon: {
        display: 'flex',
        position: 'relative',
        flexShrink: 0,
    },
    checkmarkContainer: {
        position: 'absolute',
        top: -4,
        right: -4,
        width: 20,
        height: 20,
        backgroundColor: Colors.purple,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    connectionContent: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        marginRight: 'auto',
    },
    connectionName: {
        fontSize: 16,
        fontWeight: 600,
        color: Colors.black,
    },
    connectionSubtitle: {
        fontSize: 14,
        color: Colors.grey,
    },
    connectionConnectedText: {
        fontSize: 14,
        color: Colors.purple,
        fontWeight: 500,
    },
    connectionActions: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    removeConnectionButton: {
        cursor: 'pointer',
        padding: 4,
        borderRadius: 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background-color 0.2s ease',
    },
};

export default ConnectionsList;
